import { createRouter, createWebHistory } from 'vue-router'
import MicroContainer from '@/Layout/MicroContainer.vue'
import { getLocalstorage, getCookie } from '@/utils'
import { PUBLIC_PATHS } from '@/utils/const'

const routes = [
  {
    path: '/register',
    name: 'register',
    component: () => import(/* webpackChunkName: "Login" */'@/views/login/Index.vue'),
    hidden: true
  },
  { path: '/', redirect: '/portal' },
  {
    path: '/portal',
    name: 'portal',
    component: () => import(/* webpackChunkName: "Layout" */ '@/Layout/Layout.vue'),
    redirect: '/portal/micro-component-card/render',
    children: [
      {
        // 匹配子应用 /:subPath(.*)*
        path: 'micro-:id/:subPath(.*)*',
        name: 'micro',
        component: MicroContainer
      },
      // 例如 /portal/log-admin/home 等不以 micro- 开头的微应用路径
      // 确保 Layout 正常渲染，qiankun 的 activeRule 能独立激活微应用
      {
        path: 'log-admin/:subPath(.*)*',
        name: 'micro-log',
        component: MicroContainer
      },
      {
        path: 'hello',
        name: 'hello',
        component: () => import(/* webpackChunkName: "HelloWorld" */'@/components/HelloWorld.vue')
      },
      {
        path: 'about',
        name: 'about',
        component: () => import(/* webpackChunkName: "AboutView" */ '@/components/AboutView.vue')
      },
      // 404 页面本身
      {
        path: '404',
        name: 'NotFound',
        component: () => import(/* webpackChunkName: "NotFound" */ '@/components/404.vue')
      },
      // Layout 下未匹配到任何子路由时兜底到 404。
      // 注意：微应用自己的深层路由（如 /portal/log-admin/settings/user）会先命中上面的
      // `log-admin/:subPath(.*)*`，不会走到这里。
      {
        path: ':pathMatch(.*)*',
        name: 'PortalNotFound',
        redirect: { name: 'NotFound' }
      }
    ]
  },
  // 顶层兜底：访问 /portal 之外的任何路径（例如 /foo/bar）都跳 404。
  {
    path: '/:pathMatch(.*)*',
    name: 'RootNotFound',
    redirect: '/portal/404'
  }
]

const router = createRouter({
  history: createWebHistory(),
  // 关键点在 scrollBehavior: () => new Promise(resolve => resolve({ top: 0 })) — vue-router4 的 scroll 行为是异步的，会额外触发一次 history 状态变更
  // scrollBehavior: () => new Promise(resolve => resolve({ top: 0 })),
  scrollBehavior: () => ({ top: 0 }),
  routes
})

// -----------------------------------------------------------------------------
// 拦截 push/replace，防止微应用 vue-router 污染 history.state.current 后
// 主应用 push 内部第一步 replaceState 时用错短路径导致的 URL 被削。
//
// 背景：vue-router 4 的 push 内部会先做一次
//   changeLocation(currentState.current, currentState, true)   // replaceState
// 其中 currentState = { ...historyState.value, ...history.state, ... }。
// 由于主应用与微应用共享同一个 window.history，微应用（例如 base=/portal/micro-component-card
// 的 micro-component-card）挂载时会把 history.state.current 写成短路径 /calculator。
// 主应用 push 时读到这个短路径 → replaceState 到 /calculator → URL 被污染 →
// 触发一次多余的 qiankun reroute（所有 activeRule 都返回 false），
// 紧接着真正的 pushState 又触发一次 reroute，两次 reroute 挤在一起会让下游微应用
// 的 mount 卡住（表现为 log-admin 长时间 loading）。
//
// 修复：在 push/replace 之前，用主应用 vue-router 自己保存的 state 覆盖 window.history.state，
// 保证 push 内部第一步 replaceState 用的是主应用的完整路径。
// -----------------------------------------------------------------------------
const resyncMainHistoryState = () => {
  const mainState = router.options.history.state
  if (mainState && typeof mainState === 'object' && (mainState as any).current) {
    // 只有当 window.history.state.current 与主应用记录的 current 不一致时才修正，
    // 避免不必要的 replaceState（也不会触发额外的 qiankun reroute，因为 URL 未变）。
    if ((window.history.state as any)?.current !== (mainState as any).current) {
      window.history.replaceState(mainState, '', window.location.href)
    }
  }
}
// 包装 push/replace
const originalPush = router.push.bind(router)
const originalReplace = router.replace.bind(router)
router.push = ((to: any) => {
  resyncMainHistoryState()
  return originalPush(to)
}) as typeof router.push
router.replace = ((to: any) => {
  resyncMainHistoryState()
  return originalReplace(to)
}) as typeof router.replace

// 基座应用路由守卫 — 同步检查 token，不发异步请求，避免多触发 activeRule
router.beforeEach((to, _from, next) => {
  // 登录页放行
  if (to.name === 'register') return next()

  // 展示页放行，无需 token
  if (PUBLIC_PATHS.some(p => to.path === p || to.path.startsWith(p + '/'))) return next()
  const token = getCookie('x-token') || getLocalstorage('x-token')

  // 未登录：跳登录页并把当前完整路径存到 ?redirect=... 里，登录成功后可跳回
  if (!token) {
    const redirect = encodeURIComponent(to.fullPath)
    window.location.replace(`/register?redirect=${redirect}`)
    return
  }
  next()
})

// router.beforeEach(async (to, from, next) => {
//   // 点击浏览器的回退和前进，子应用会出现路由无法匹配的情况 回退时，如果匹配的是子应用，子应用中会丢失activeRule
//   history.replaceState({ back: from.path }, to.name as string || '', '')
//   // 修复通过菜单切换后（主站使用的为vue-router3.x）导致 history.state中丢失关键信息
//   // 再次通过vue-router4.x 的 router进行切换时，activeRule会先触发一个丢失关键信息的路由 http://localhost:5001/home
//   if (!history.state.current) {
//     history.state.current = window.location.pathname
//   }
//   next()
// })

export default router
